import {
  errorMap,
  Form,
  Main,
  Padding,
  Stack,
  TotalEntries,
  useDirectory,
  DataObjectDescriptionFilter,
  zValidPeriodWithBothRequired,
  TimeSeriesFilters,
  VISUAL_VALIDATION_FILTER_KEYS,
  EMPTY_PAGE,
  useValidationDashboardsQuery,
  omit,
  DashboardType,
  CustomPagination,
  type Frequency,
  useDataObjectGroupsWithDescriptionsQuery,
  DataObjectGroupType,
  useGroupDescriptionOptions,
  useGenerateVisualValidationTimeSeriesComparisonMutation,
  useLatestDataObjectVersionsQuery,
  useDuplicateVisualValidationTimeSeriesComparisonMutation,
  VisualValidationFilters,
  type ISODate,
  useChangeVisualValidationTimeSeriesComparisonDataMutation,
  useVisualValidationDashboardActions,
  ValidationDashboardListDto,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import styled from 'styled-components';
import { path } from '../../../paths';

import translations from './TimeSeriesComparison.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import z from 'zod/v3';
import { Divider, Flex } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { VisualValidationHeader } from '../VisualValidationHeader';
import { VisualValidationActionButtons } from '../VisualValidationActionButtons';
import { keepPreviousData } from '@tanstack/react-query';
import { ValidationDashboardsTable } from '../ValidationDashboardsTable';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  AlgorithmsModal,
  ModalData,
  SourcesModal,
} from '../modals/DetailsModal';
import { CommentsModal, hasDescriptionId } from '../modals/CommentsModal';

const { addTranslations, t } = i18nNsFactory(
  'visual-validation-time-series-comparison',
);
addTranslations(translations);

type TimeSeriesComparisonFilters = VisualValidationFilters & {
  valueDateFrom?: string;
  valueDateTo?: string;
  versionRule?: string;
  versionRule2?: string;
};

interface TimeSeriesComparisonRequest {
  valueDateFrom: ISODate;
  valueDateTo: ISODate;
  dataObjectVersionIds1: number[];
  dataObjectVersionIds2: number[];
  rule1Code: string;
  rule2Code: string;
  outliers: boolean;
  hp: boolean;
}

const FILTER_KEYS: (keyof TimeSeriesComparisonFilters)[] = [
  ...VISUAL_VALIDATION_FILTER_KEYS,
  'valueDateFrom',
  'valueDateTo',
  'versionRule',
  'versionRule2',
];

export const TimeSeriesComparisonFormSchema = z
  .object({
    groupDescriptionRef: z
      .string(errorMap(() => t('validation.groupDescriptionRef.required')))
      .min(1),
    valueDateRange: zValidPeriodWithBothRequired(),
    versionRule: z
      .string(errorMap(() => t('validation.versionRule.required')))
      .min(1),
    versionRule2: z
      .string(errorMap(() => t('validation.versionRule2.required')))
      .min(1),
    dataObjectVersionIds1: z.number().int().positive().array().optional(),
    dataObjectVersionIds2: z.number().int().positive().array().optional(),
    outliers: z.boolean().optional(),
    hodricPrescotti: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      !Array.isArray(data.dataObjectVersionIds1) ||
      !data.dataObjectVersionIds1.length
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['versionRule'],
        message: t('validation.versionRule.noVersionsFound'),
      });
    }
    if (
      !Array.isArray(data.dataObjectVersionIds2) ||
      !data.dataObjectVersionIds2.length
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['versionRule2'],
        message: t('validation.versionRule2.noVersionsFound'),
      });
    }
  });

type TimeSeriesComparisonFormType = z.infer<
  typeof TimeSeriesComparisonFormSchema
>;

export const TimeSeriesComparison = () => (
  <Form
    schema={TimeSeriesComparisonFormSchema}
    blockNavigationOnFirstChange={false}
  >
    <Main>
      <TimeSeriesComparisonInner />
    </Main>
  </Form>
);

const TimeSeriesComparisonInner = () => {
  const directoryProps = useDirectory({
    queryFilterKeys: FILTER_KEYS,
  });
  const {
    queryParameters,
    setQueryFilters,
    tableProps,
    headerProps,
    setTableFilters,
    paginationProps,
  } = directoryProps;

  const { handleSubmit, getValues, trigger } =
    useFormContext<TimeSeriesComparisonFormType>();
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [algorithmsModalData, setAlgorithmsModalData] =
    useState<ModalData | null>(null);
  const [sourcesModalData, setSourcesModalData] = useState<ModalData | null>(
    null,
  );
  const [frequency, setFrequency] = useState<Frequency | undefined>(undefined);

  const {
    valueDateRange,
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    dataObjectVersions1,
    isFetchingDataObjectVersionIds1,
    dataObjectVersions2,
    isFetchingDataObjectVersionIds2,
    refetchDescriptions,
  } = useTimeSeriesComparisonSelection(queryParameters, setQueryFilters);
  const descriptionId = selectedDescription?.descriptionId;

  const {
    data: { content, totalElements } = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useValidationDashboardsQuery(
    {
      ...omit(queryParameters, ['groupDescriptionRef']),
      dataObjectDescriptionId: descriptionId!,
      dashboardType: DashboardType.TIME_SERIES_COMPARISON,
    },
    {
      placeholderData: keepPreviousData,
      enabled: !!descriptionId,
    },
  );

  const getRequestData = useCallback(() => {
    const [
      valueDateRange,
      dataObjectVersionIds1,
      dataObjectVersionIds2,
      rule1Code,
      rule2Code,
      outliers,
      hodricPrescotti,
    ] = getValues([
      'valueDateRange',
      'dataObjectVersionIds1',
      'dataObjectVersionIds2',
      'versionRule',
      'versionRule2',
      'outliers',
      'hodricPrescotti',
    ]);
    return {
      valueDateFrom: valueDateRange[0],
      valueDateTo: valueDateRange[1],
      dataObjectVersionIds1: dataObjectVersionIds1!,
      dataObjectVersionIds2: dataObjectVersionIds2!,
      rule1Code,
      rule2Code,
      outliers: Boolean(outliers),
      hp: Boolean(hodricPrescotti),
    };
  }, [getValues]);

  const openDetailsModal = useCallback(
    (
      modalType: 'algorithms' | 'sources',
      rowData?: ValidationDashboardListDto,
    ) => {
      if (!selectedDescription) return;
      const versions = rowData
        ? rowData.versions
            .filter((v) => v.sequenceNumber === 1)
            .map((v) => v.dataObjectVersion)
        : dataObjectVersions1;
      const versionsOther = rowData
        ? rowData.versions
            .filter((v) => v.sequenceNumber === 2)
            .map((v) => v.dataObjectVersion)
        : dataObjectVersions2;
      if (!rowData && versions.length === 0 && versionsOther.length === 0)
        return;
      const data = {
        type: DashboardType.TIME_SERIES_COMPARISON,
        versions,
        versionsOther,
        descriptionOption: selectedDescription,
      } as const;
      if (modalType === 'algorithms') setAlgorithmsModalData(data);
      else setSourcesModalData(data);
    },
    [selectedDescription, dataObjectVersions1, dataObjectVersions2],
  );

  const {
    createDashboard,
    openDashboard,
    duplicateDashboard,
    changeDashboardData,
  } = useVisualValidationDashboardActions<TimeSeriesComparisonRequest>({
    hasAccess: selectedDescription?.hasAccess || false,
    popupWindowPath: path.visualValidationGenerating,
    getRequestData,
    useCreate: useGenerateVisualValidationTimeSeriesComparisonMutation,
    useDuplicate: useDuplicateVisualValidationTimeSeriesComparisonMutation,
    useChangeData: useChangeVisualValidationTimeSeriesComparisonDataMutation,
    onValidationFailed: trigger,
    onSuccess: refetch,
    getDashboardType,
  });

  return (
    <Main.Col>
      <Main.Header />
      <Main.Content
        padding={false}
        id="visual-validation-time-series-comparison"
      >
        <ContentWrapper>
          {isCommentsModalOpen && selectedDescription && (
            <CommentsModal
              descriptionOption={selectedDescription}
              type="range"
              valueDates={valueDateRange}
              refetchDescriptions={refetchDescriptions}
              onClose={() => setIsCommentsModalOpen(false)}
            />
          )}
          {algorithmsModalData && (
            <AlgorithmsModal
              {...algorithmsModalData}
              onClose={() => setAlgorithmsModalData(null)}
            />
          )}
          {sourcesModalData && (
            <SourcesModal
              {...sourcesModalData}
              onClose={() => setSourcesModalData(null)}
            />
          )}
          <VisualValidationHeader
            infoIconUrl={InfoIconUrl.VISUAL_VALIDATION_TIME_SERIES_COMPARISON}
          />

          <Padding horizontal>
            <DataObjectDescriptionFilter
              onFrequencyChange={(frequency) => {
                setFrequency(frequency);
              }}
              options={descriptionOptions}
              isLoading={isFetchingDescriptions}
            />
          </Padding>
          <Divider />
          <Padding horizontal bottom>
            <Flex gap="small" wrap="wrap" justify="space-between">
              <Flex gap="small" wrap="wrap">
                <TimeSeriesFilters
                  isComparisonView
                  disabled={!frequency}
                  frequency={frequency}
                  isTimeSeriesView
                />
              </Flex>
              <VisualValidationActionButtons
                hasDescription={!!descriptionId}
                hasVersions={
                  dataObjectVersions1.length > 0 ||
                  dataObjectVersions2.length > 0
                }
                hasComments={selectedDescription?.hasComments || false}
                onCommentsClick={() => {
                  setIsCommentsModalOpen(true);
                }}
                onAlgorithmsClick={() => openDetailsModal('algorithms')}
                onSourcesClick={() => openDetailsModal('sources')}
                onNewDashboardClick={() => handleSubmit(createDashboard)()}
                filtersCount={headerProps.filtersCount}
                onFilterClear={headerProps.onFilterClear}
                isLoading={
                  isFetchingDataObjectVersionIds1 ||
                  isFetchingDataObjectVersionIds2
                }
              />
            </Flex>
          </Padding>
          <Padding horizontal bottom>
            <Flex vertical gap="small">
              <ValidationDashboardsTable
                data={content || []}
                isLoading={isFetching}
                setFilters={setTableFilters}
                scrollContainerSelector="#visual-validation-time-series-comparison"
                refetchData={refetch}
                onClickOpen={openDashboard}
                onClickDuplicate={duplicateDashboard}
                onClickChangeData={changeDashboardData}
                onClickSourceData={(d) => openDetailsModal('sources', d)}
                onClickAlgorithmData={(d) => openDetailsModal('algorithms', d)}
                {...tableProps}
              />
            </Flex>
          </Padding>
        </ContentWrapper>
      </Main.Content>
      <Main.Footer>
        <TotalEntries count={totalElements} />
        <FooterPagination {...paginationProps} />
      </Main.Footer>
    </Main.Col>
  );
};

const useTimeSeriesComparisonSelection = (
  queryParameters: TimeSeriesComparisonFilters,
  setQueryFilters: (filters: Partial<TimeSeriesComparisonFilters>) => void,
) => {
  const { setValue, getValues } =
    useFormContext<TimeSeriesComparisonFormType>();
  const {
    groupDescriptionRef,
    valueDateRange,
    versionRule,
    versionRule2,
    dataObjectVersionIds1,
    dataObjectVersionIds2,
    outliers,
    hodricPrescotti,
  } = useWatch<TimeSeriesComparisonFormType>();
  const { reset } = useFormContext<TimeSeriesComparisonFormType>();

  // Update query parameters when form values change
  useEffect(() => {
    setQueryFilters({
      groupDescriptionRef,
      valueDateFrom: valueDateRange?.[0],
      valueDateTo: valueDateRange?.[1],
      versionRule,
      versionRule2,
    });
  }, [
    groupDescriptionRef,
    setQueryFilters,
    valueDateRange,
    versionRule,
    versionRule2,
  ]);

  // Reset form values when query parameters change
  useEffect(() => {
    const valueDateFromParam = queryParameters?.valueDateFrom;
    const valueDateToParam = queryParameters?.valueDateTo;
    const versionRuleParam = queryParameters?.versionRule;
    const versionRule2Param = queryParameters?.versionRule2;

    reset({
      ...queryParameters,
      valueDateRange: [
        (valueDateFromParam as ISODate) || undefined,
        (valueDateToParam as ISODate) || undefined,
      ],
      versionRule: versionRuleParam,
      versionRule2: versionRule2Param,
      outliers,
      hodricPrescotti,
    });
  }, [queryParameters, reset, getValues, outliers, hodricPrescotti]);

  const {
    data: dataObjectGroupsWithDescriptions,
    refetch: refetchDescriptions,
    isFetching: isFetchingDescriptions,
  } = useDataObjectGroupsWithDescriptionsQuery(
    {
      groupType: DataObjectGroupType.O,
    },
    {
      placeholderData: keepPreviousData,
    },
  );
  const descriptionOptions = useGroupDescriptionOptions(
    dataObjectGroupsWithDescriptions,
    false,
  );

  const selectedDescription = useMemo(
    () =>
      descriptionOptions
        .flatMap((options) => options)
        .filter(hasDescriptionId)
        .find((option) => option.value === groupDescriptionRef),
    [descriptionOptions, groupDescriptionRef],
  );
  const descriptionId = selectedDescription?.descriptionId;

  const {
    data: dataObjectVersions1 = [],
    isFetching: isFetchingDataObjectVersionIds1,
  } = useLatestDataObjectVersionsQuery(
    {
      descriptionId: descriptionId!,
      valueDateFrom: valueDateRange?.[0]!,
      valueDateTo: valueDateRange?.[1]!,
      ruleCode: versionRule!,
    },
    {
      enabled: Boolean(
        descriptionId &&
          valueDateRange?.[0] &&
          valueDateRange?.[1] &&
          versionRule,
      ),
    },
  );

  useEffect(() => {
    const newVersionIds = dataObjectVersions1.map((ver) => ver.id);
    const same =
      dataObjectVersionIds1 &&
      dataObjectVersionIds1.length === newVersionIds.length &&
      dataObjectVersionIds1.every((id, index) => id === newVersionIds[index]);
    if (!same) {
      setValue('dataObjectVersionIds1', newVersionIds);
    }
  }, [setValue, dataObjectVersionIds1, dataObjectVersions1]);

  const {
    data: dataObjectVersions2 = [],
    isFetching: isFetchingDataObjectVersionIds2,
  } = useLatestDataObjectVersionsQuery(
    {
      descriptionId: descriptionId!,
      valueDateFrom: valueDateRange?.[0]!,
      valueDateTo: valueDateRange?.[1]!,
      ruleCode: versionRule2!,
    },
    {
      enabled: Boolean(
        descriptionId &&
          valueDateRange?.[0] &&
          valueDateRange?.[1] &&
          versionRule2,
      ),
    },
  );

  useEffect(() => {
    const newVersionIds = dataObjectVersions2.map((ver) => ver.id);
    const same =
      dataObjectVersionIds2 &&
      dataObjectVersionIds2.length === newVersionIds.length &&
      dataObjectVersionIds2.every((id, index) => id === newVersionIds[index]);
    if (!same) {
      setValue('dataObjectVersionIds2', newVersionIds);
    }
  }, [setValue, dataObjectVersionIds2, dataObjectVersions2]);

  return {
    valueDateRange,
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    refetchDescriptions,
    dataObjectVersions1,
    isFetchingDataObjectVersionIds1,
    dataObjectVersions2,
    isFetchingDataObjectVersionIds2,
  };
};

function getDashboardType(request: TimeSeriesComparisonRequest) {
  if (request.hp) {
    return DashboardType.HODRICK_PRESCOTT_COMPARISON;
  }
  if (request.outliers) {
    return DashboardType.OUTLIERS_COMPARISON;
  }

  return DashboardType.TIME_SERIES_COMPARISON;
}

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
